import { endOfDay, format, hoursToMilliseconds, startOfDay } from 'date-fns'

import { formatSortedIsoDateStringsRange } from '../../../helpers/date'
import { EstimateValue, PerformanceChange } from '../types/TrackingEvents'

type CalculateEventPerformanceChangesParams = {
  eventStart: number
  eventEnd: number
  performanceChanges?: PerformanceChange[]
  performanceValues?: EstimateValue[]
}

type EventPerformanceChanges = {
  REVENUE_DAY_1_CHANGE?: number
  DOWNLOAD_DAY_1_CHANGE?: number
  DAU_DAY_1_CHANGE?: number
}

export type EventKpiPeriodExplanation =
  | {
      variant: 'multi_day_average'
      duringRangeFormatted: string
      beforeRangeFormatted: string
      ongoing: boolean
    }
  | {
      variant: 'single_day_prior'
      eventDayFormatted: string
    }

const average = (values: number[]): number => {
  if (values.length === 0) return 0
  return values.reduce((sum, v) => sum + v, 0) / values.length
}

/**
 * Compute % change between `during` and `before`, returning ±Infinity when
 * the before period was zero but during had movement. Callers can render this
 * via `TrendIndicator allowInfinity` to surface a directional arrow without a
 * misleading "0%" reading.
 */
const computePercentChange = (during: number, before: number): number => {
  if (before !== 0) return (during - before) / before
  if (during > 0) return Infinity
  if (during < 0) return -Infinity
  return 0
}

/**
 * Resolve a single-day day-over-day change for KPI chips. Recovers changes
 * the backend cannot deliver from the daily values in two directions:
 * - yesterday zero → today non-zero: the true change is ±Infinity which cannot
 *   be serialized in JSON, so we surface the direction as ±Infinity ourselves.
 * - yesterday non-zero → today zero, with the backend value missing: the true
 *   change is a full -100% drop, so we return -1 instead of hiding the chip
 *   (GR-2984). A present backend value is never overridden in this direction.
 *
 * Otherwise falls back to the backend-provided value (which may itself be
 * undefined when both days are zero or one of the values is missing).
 */
const resolveDay1Change = (backendValue: number | undefined, todayValue: number | undefined, yesterdayValue: number | undefined): number | undefined => {
  if (typeof todayValue === 'number' && typeof yesterdayValue === 'number') {
    if (yesterdayValue === 0 && todayValue !== 0) {
      return todayValue > 0 ? Infinity : -Infinity
    }

    if (backendValue == null && yesterdayValue !== 0 && todayValue === 0) {
      return -1
    }
  }

  return backendValue
}

type KpiDateWindows = {
  duringDates: string[]
  beforeDates: string[]
}

const buildKpiDateWindows = (startTime: Date, windowEnd: Date): KpiDateWindows => {
  const duringDates: string[] = []
  const cursor = new Date(startTime)
  while (cursor <= windowEnd) {
    duringDates.push(format(cursor, 'yyyy-MM-dd'))
    cursor.setDate(cursor.getDate() + 1)
  }
  const beforeDates: string[] = []
  const beforeCursor = new Date(startTime)
  for (let i = 0; i < duringDates.length; i++) {
    beforeCursor.setDate(beforeCursor.getDate() - 1)
    beforeDates.push(format(beforeCursor, 'yyyy-MM-dd'))
  }
  return { duringDates, beforeDates }
}

export const getEventKpiPeriodExplanation = ({ eventStart, eventEnd }: CalculateEventPerformanceChangesParams): EventKpiPeriodExplanation | undefined => {
  const startTime = startOfDay(new Date(eventStart))
  const endTime = endOfDay(new Date(eventEnd))

  const eventDuration = endTime.getTime() - startTime.getTime()
  const isMultiDay = eventDuration > hoursToMilliseconds(24)
  const eventHasEnded = endTime.getTime() < Date.now()

  if (isMultiDay) {
    if (startTime.getTime() > Date.now()) return undefined

    const { duringDates, beforeDates } = buildKpiDateWindows(startTime, endTime)

    return {
      variant: 'multi_day_average',
      duringRangeFormatted: formatSortedIsoDateStringsRange(duringDates),
      beforeRangeFormatted: formatSortedIsoDateStringsRange(beforeDates),
      ongoing: !eventHasEnded,
    }
  } else {
    if (startTime.getTime() > Date.now()) return undefined
    return {
      variant: 'single_day_prior',
      eventDayFormatted: format(startTime, 'MMM d, yyyy'),
    }
  }
}

/**
 * Calculates performance changes for an event based on its duration.
 * Only runs for ended events — ongoing events always return undefined.
 * - For multi-day events: avg daily value during event vs avg of same-length window before event
 * - For single-day events: uses existing day-over-day changes from API
 */
export const calculateEventPerformanceChanges = ({
  eventStart,
  eventEnd,
  performanceChanges,
  performanceValues,
}: CalculateEventPerformanceChangesParams): EventPerformanceChanges | undefined => {
  const startTime = startOfDay(new Date(eventStart))
  const endTime = endOfDay(new Date(eventEnd))

  const eventDuration = endTime.getTime() - startTime.getTime()
  const isMultiDay = eventDuration > hoursToMilliseconds(24)
  const eventHasEnded = endTime.getTime() < Date.now()

  if (isMultiDay && performanceValues) {
    if (!eventHasEnded) return undefined

    const { duringDates, beforeDates } = buildKpiDateWindows(startTime, endTime)

    const valuesByDate: Record<string, EstimateValue> = Object.fromEntries(performanceValues.map((pv) => [pv.date, pv]))
    const duringValues = duringDates.map((d) => valuesByDate[d]).filter(Boolean) as EstimateValue[]
    const beforeValues = beforeDates.map((d) => valuesByDate[d]).filter(Boolean) as EstimateValue[]

    if (duringValues.length === 0 || beforeValues.length === 0) return undefined

    const avgDuringRevenue = average(duringValues.map((pv) => pv.revenue))
    const avgBeforeRevenue = average(beforeValues.map((pv) => pv.revenue))
    const avgDuringDownloads = average(duringValues.map((pv) => pv.downloads))
    const avgBeforeDownloads = average(beforeValues.map((pv) => pv.downloads))

    const duringDau = duringValues.map((pv) => pv.dailyActiveUsers).filter((v): v is number => v !== undefined)
    const beforeDau = beforeValues.map((pv) => pv.dailyActiveUsers).filter((v): v is number => v !== undefined)
    const avgDuringDau = duringDau.length > 0 ? average(duringDau) : undefined
    const avgBeforeDau = beforeDau.length > 0 ? average(beforeDau) : undefined
    const dauChange = avgDuringDau !== undefined && avgBeforeDau !== undefined ? computePercentChange(avgDuringDau, avgBeforeDau) : undefined

    return {
      REVENUE_DAY_1_CHANGE: computePercentChange(avgDuringRevenue, avgBeforeRevenue),
      DOWNLOAD_DAY_1_CHANGE: computePercentChange(avgDuringDownloads, avgBeforeDownloads),
      DAU_DAY_1_CHANGE: dauChange,
    }
  } else if (!isMultiDay && performanceChanges) {
    const eventStartDate = format(startTime, 'yyyy-MM-dd')
    const matchedPerformanceChange = performanceChanges.find((pc) => pc.date === eventStartDate)
    if (!matchedPerformanceChange?.performanceChanges) return undefined
    const { REVENUE_DAY_1_CHANGE, DOWNLOAD_DAY_1_CHANGE, DAU_DAY_1_CHANGE } = matchedPerformanceChange.performanceChanges

    // Single-day events use backend's day-over-day change values. When the
    // prior day was 0 (e.g. soft-launched game with no installs yet), backend
    // can't represent ±Infinity in JSON and typically returns null/undefined,
    // which would hide the change chip. Cross-reference performanceValues to
    // recover the direction (today vs yesterday) and emit ±Infinity so the
    // KPI chip renders a directional indicator instead of being skipped.
    const valuesByDate: Record<string, EstimateValue> = performanceValues ? Object.fromEntries(performanceValues.map((pv) => [pv.date, pv])) : {}
    const today = valuesByDate[eventStartDate]
    const yesterdayDate = format(new Date(startTime.getTime() - 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
    const yesterday = valuesByDate[yesterdayDate]

    return {
      REVENUE_DAY_1_CHANGE: resolveDay1Change(REVENUE_DAY_1_CHANGE, today?.revenue, yesterday?.revenue),
      DOWNLOAD_DAY_1_CHANGE: resolveDay1Change(DOWNLOAD_DAY_1_CHANGE, today?.downloads, yesterday?.downloads),
      DAU_DAY_1_CHANGE: resolveDay1Change(DAU_DAY_1_CHANGE, today?.dailyActiveUsers, yesterday?.dailyActiveUsers),
    }
  }

  return undefined
}
